import { api } from "./client";
import type {
  SalesByPeriodsMode,
  SalesByPeriodsUnit,
} from "./reportsSalesByPeriods";

// Трите справки за резервации. BE: app/features/reports/router_reservations.py
// (`/reports/analytics/reservations/*`), по едно право на справка.
// Периодът е по НАЧАЛОТО на резервацията; полетата са snake_case като
// останалите справки, не camelCase като модула „Резервации".

export type ReservationReportStatus =
  | "upcoming"
  | "seated"
  | "completed"
  | "cancelled"
  | "expired";

/** Общите броячи по статус — във всеки ред и обобщение на трите справки. */
export interface ReservationStatusCounts {
  reservations: number;
  upcoming: number;
  seated: number;
  completed: number;
  cancelled: number;
  expired: number;
}

/** Една резервация в „Дневник". `bill_*` не се крият — със и без втори код. */
export interface ReservationJournalRow {
  id: number;
  start_at: string;
  end_at: string;
  duration_minutes: number;
  guests: number;
  guest_name: string;
  phone: string;
  note: string | null;
  status: ReservationReportStatus;
  table_ids: number[];
  table_names: string[];
  created_at: string;
  created_by: number | null;
  created_by_name: string | null;
  seated_by: number | null;
  seated_by_name: string | null;
  /** Авторът на ПЪРВАТА поръчка по сметката — колоната „Настанена от".
   *  null — няма сметка или няма поръчки.
   *  Не зависи от прехвърлянето на сметката (за разлика от `seated_by`). */
  first_order_by: number | null;
  first_order_by_name: string | null;
  seated_at: string | null;
  released_at: string | null;
  /** Настаняване − планирано начало; отрицателно = дошли по-рано. */
  arrival_offset_minutes: number | null;
  /** Реален престой (приключване − настаняване). */
  seated_minutes: number | null;
  bill_id: number | null;
  bill_name: string | null;
  /** Decimal от BE → JSON низ. Стари данни с две резервации на една сметка:
   *  сумата е само на реда, който носи оборота (другият — null). */
  bill_total: string | null;
}

export interface ReservationJournalSummary extends ReservationStatusCounts {
  guests: number;
  revenue: string;
}

export interface ReservationJournalResponse {
  rows: ReservationJournalRow[];
  summary: ReservationJournalSummary;
}

/** Един отрязък от времето на „Натовареност" — ключът и полетата за етикета
 *  са СЪЩИТЕ като в „Продажби по периоди" (`periodLabelOf`): при `grouped`
 *  само едно от `hour`/`weekday`(понеделник = 0)/`month`, при `timeline` —
 *  `date`+`hour`, `date`, `year`+`month` или `year`. */
export interface ReservationOccupancyRow extends ReservationStatusCounts {
  key: string;
  hour: number | null;
  weekday: number | null;
  month: number | null;
  year: number | null;
  date: string | null;
  guests: number;
  avg_planned_minutes: number | null;
  avg_seated_minutes: number | null;
}

export interface ReservationOccupancySummary extends ReservationStatusCounts {
  guests: number;
}

export interface ReservationOccupancyResponse {
  mode: SalesByPeriodsMode;
  unit: SalesByPeriodsUnit;
  rows: ReservationOccupancyRow[];
  summary: ReservationOccupancySummary;
}

/** Един гост = един нормализиран телефон; името е от най-новата резервация. */
export interface ReservationGuestRow extends ReservationStatusCounts {
  phone_key: string;
  phone: string;
  guest_name: string;
  guests: number;
  first_reservation_at: string;
  last_reservation_at: string;
  last_visit_at: string | null;
  revenue: string;
}

export interface ReservationGuestsSummary {
  guests_count: number;
  reservations: number;
  returning_guests: number;
  revenue: string;
}

export interface ReservationGuestsResponse {
  rows: ReservationGuestRow[];
  summary: ReservationGuestsSummary;
}

/** Показателите на един гост. Средните са само по ПРИКЛЮЧИЛИТЕ посещения
 *  (настанената още тече, отказаната и неизпълнената нямат сметка);
 *  `revenue` е същото число като в реда на „Гости". */
export interface ReservationGuestVisitsSummary extends ReservationStatusCounts {
  /** Настанени + приключили — гостът е дошъл. */
  visits: number;
  revenue: string;
  average_bill: string | null;
  average_stay_minutes: number | null;
  last_visit_at: string | null;
}

/** Историята на един гост (модалът при клик на ред в „Гости") — неговите
 *  резервации от периода като редове на „Дневник", най-новата първа. */
export interface ReservationGuestVisitsResponse {
  phone_key: string;
  phone: string | null;
  guest_name: string | null;
  rows: ReservationJournalRow[];
  summary: ReservationGuestVisitsSummary;
}

export interface ReservationReportFilters {
  start_date?: string;
  end_date?: string;
}

/** Телефонът в какъвто и да е запис — BE сравнява по нормализирания ключ. */
export interface ReservationGuestVisitsFilters extends ReservationReportFilters {
  phone: string;
}

export interface ReservationOccupancyFilters extends ReservationReportFilters {
  mode: SalesByPeriodsMode;
  unit: SalesByPeriodsUnit;
  statuses?: ReservationReportStatus[];
  table_ids?: number[];
}

export const getReservationsJournalReport = async (
  filters: ReservationReportFilters,
): Promise<ReservationJournalResponse> => {
  const response = await api.get<ReservationJournalResponse>(
    "/reports/analytics/reservations/journal",
    { params: filters },
  );
  return response.data;
};

export const getReservationsOccupancyReport = async (
  filters: ReservationOccupancyFilters,
): Promise<ReservationOccupancyResponse> => {
  const params = new URLSearchParams();
  if (filters.start_date) params.append("start_date", filters.start_date);
  if (filters.end_date) params.append("end_date", filters.end_date);
  params.append("mode", filters.mode);
  params.append("unit", filters.unit);
  for (const status of filters.statuses ?? []) params.append("statuses", status);
  for (const id of filters.table_ids ?? []) params.append("table_ids", String(id));

  const response = await api.get<ReservationOccupancyResponse>(
    `/reports/analytics/reservations/occupancy?${params.toString()}`,
  );
  return response.data;
};

export const getReservationsGuestsReport = async (
  filters: ReservationReportFilters,
): Promise<ReservationGuestsResponse> => {
  const response = await api.get<ReservationGuestsResponse>(
    "/reports/analytics/reservations/guests",
    { params: filters },
  );
  return response.data;
};

export const getReservationGuestVisits = async (
  filters: ReservationGuestVisitsFilters,
): Promise<ReservationGuestVisitsResponse> => {
  const response = await api.get<ReservationGuestVisitsResponse>(
    "/reports/analytics/reservations/guests/visits",
    { params: filters },
  );
  return response.data;
};
